import React from 'react';
import { alpha, Box, Chip, Paper, Stack, Typography } from '@mui/material';
import {
  HelpOutline,
  ChatBubbleOutline,
  PlayCircleOutline,
  Science,
  Calculate,
  Label,
} from '@mui/icons-material';

const steps = [
  {
    title: 'Open the formulation workspace',
    icon: <Science sx={{ fontSize: 16 }} />,
  },
  {
    title: 'Generate nutrition from ingredients',
    icon: <Calculate sx={{ fontSize: 16 }} />,
  },
  {
    title: 'Review labels and calculated values',
    icon: <Label sx={{ fontSize: 16 }} />,
  },
];

export default function PlatformHelpVisual() {
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        minHeight: 280,
        p: 2,
        bgcolor: 'background.paper',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          width: '100%',
          borderRadius: 2.5,
          overflow: 'hidden',
          bgcolor: 'background.paper',
          boxShadow: '0 16px 32px rgba(15, 23, 42, 0.05)',
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: '50%',
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <HelpOutline sx={{ fontSize: 18 }} />
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                Guided support
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Step-by-step workflow guidance
              </Typography>
            </Box>
          </Stack>

          <Chip
            label="Guided help"
            size="small"
            sx={{
              height: 24,
              fontSize: 11,
              fontWeight: 800,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
              color: 'primary.main',
            }}
          />
        </Stack>

        {/* Body */}
        <Box sx={{ p: 2 }}>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mb: 1,
              fontWeight: 800,
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: 0.4,
            }}
          >
            Example help request
          </Typography>

          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.025),
              mb: 2,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <ChatBubbleOutline sx={{ fontSize: 17, color: 'primary.main', mt: 0.2 }} />
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 800,
                  lineHeight: 1.45,
                  fontSize: 11.5,
                }}
              >
                How do I generate nutrition values from the ingredients in this formulation?
              </Typography>
            </Stack>
          </Paper>

          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mb: 1,
              fontWeight: 800,
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: 0.4,
            }}
          >
            Guided walkthrough
          </Typography>

          <Paper
            variant="outlined"
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              bgcolor: 'background.paper',
            }}
          >
            {steps.map((step, index) => (
              <Stack
                key={step.title}
                direction="row"
                spacing={1.2}
                alignItems="center"
                sx={{
                  px: 1.5,
                  py: 1.2,
                  borderBottom: index !== steps.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider',
                }}
              >
                <Box
                  sx={{
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                    color: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {step.icon}
                </Box>

                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 800,
                    fontSize: 11.5,
                  }}
                >
                  {step.title}
                </Typography>

                <Box sx={{ ml: 'auto' }}>
                  <PlayCircleOutline sx={{ fontSize: 16, color: 'text.secondary' }} />
                </Box>
              </Stack>
            ))}
          </Paper>
        </Box>
      </Paper>
    </Box>
  );
}